// The full device inventory — every NMEA device aboard, grouped by functional
// category in a compact list. Primary identity and reporting state stay visible;
// forensic NMEA fields expand below the selected row.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useBoatChrome } from '../components/app/boatChrome';
import { playbackNowMs } from '../lib/playback';
import { AnalyzerIcon } from '../components/app/icons';
import { DeviceSections } from '../components/devices/DeviceSections';
import { groupProtocolTwins } from '../lib/deviceGrouping';
import {
  ALL_MANUFACTURERS,
  ALL_SOURCES,
  availableFilterValue,
  defaultSourceFilter,
  filterDevices,
  manufacturerFilterOptions,
  sourceFilterOptions,
  type DeviceSortKey,
  type DeviceStatusFilter,
} from '../lib/deviceInventory';
import type { VesselDevice } from '../lib/useBoatDevices';
import { useDocumentTitle } from '../lib/useDocumentTitle';

// The inventory the screen reads while the fetch is still `loading` or has
// errored. Deliberately one shared module constant rather than a `[]` literal
// inline in the ternary below: a literal is a fresh array identity on every
// render, so while devices are loading it would invalidate all three memos
// keyed on `inventory` on every unrelated re-render (the 30s `now` tick, a
// filter keystroke). Hoisting it also silences `react-hooks/exhaustive-deps`,
// which flags exactly this shape ("the 'inventory' conditional could make the
// dependencies … change on every render"). Safe to share because every
// consumer — `manufacturerFilterOptions`, `sourceFilterOptions`,
// `filterDevices` — only reads it and returns a new array.
const NO_DEVICES: VesselDevice[] = [];

function deviceCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'device' : 'devices'}`;
}

export function Devices() {
  const { boatId, boat, devices } = useBoatChrome();
  useDocumentTitle(boat ? `${boat.display_name} — Devices` : 'Devices — Neomaris');

  // Playback-aware: on a replaying boat the device stamps are recording-time,
  // so freshness must be judged on the recording's clock (lib/playback.ts).
  const [now, setNow] = useState(() => playbackNowMs(boat?.simTime));
  const [sortBy, setSortBy] = useState<DeviceSortKey>('name');
  const [manufacturerFilter, setManufacturerFilter] = useState(ALL_MANUFACTURERS);
  const [statusFilter, setStatusFilter] = useState<DeviceStatusFilter>('all');
  // `null` = the user has not chosen a gateway yet, so the screen shows its
  // default (the primary gateway). Kept as an explicit "unchosen" sentinel
  // rather than seeding state from the inventory: the inventory arrives after
  // the first render (and again on every live refresh), and a sentinel lets the
  // default resolve late without an effect that could stomp a real choice.
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const simTime = boat?.simTime ?? null;
  useEffect(() => {
    setNow(playbackNowMs(simTime));
    const id = setInterval(() => setNow(playbackNowMs(simTime)), 30_000);
    return () => clearInterval(id);
  }, [simTime]);
  useEffect(() => {
    setSortBy('name');
    setManufacturerFilter(ALL_MANUFACTURERS);
    setStatusFilter('all');
    setSourceFilter(null);
  }, [boatId]);

  const state = devices.state;
  const inventory = state.kind === 'ready' ? state.devices : NO_DEVICES;
  const primarySourceId = state.kind === 'ready' ? state.primarySourceId : null;
  // The option lists depend only on the inventory, so memoizing keeps the
  // 30s `now` tick (and unrelated re-renders) from re-running their regex-heavy
  // source-name derivation. Only the filtered list actually consumes `now`.
  const manufacturerOptions = useMemo(() => manufacturerFilterOptions(inventory), [inventory]);
  const sourceOptions = useMemo(() => sourceFilterOptions(inventory), [inventory]);
  const activeManufacturerFilter = availableFilterValue(
    manufacturerFilter,
    ALL_MANUFACTURERS,
    manufacturerOptions
  );
  // Unchosen → every gateway. The primary is still marked in the option label,
  // but the screen no longer opens filtered to it — see `defaultSourceFilter`.
  const activeSourceFilter =
    sourceFilter === null
      ? defaultSourceFilter()
      : availableFilterValue(sourceFilter, ALL_SOURCES, sourceOptions);
  useEffect(() => {
    if (manufacturerFilter !== activeManufacturerFilter) {
      setManufacturerFilter(activeManufacturerFilter);
    }
    // Only reconcile a REAL choice whose gateway vanished from the inventory.
    // An unchosen filter stays `null` so the default keeps re-resolving as the
    // inventory (and the primary's devices) come and go.
    if (sourceFilter !== null && sourceFilter !== activeSourceFilter) {
      setSourceFilter(activeSourceFilter);
    }
  }, [manufacturerFilter, activeManufacturerFilter, sourceFilter, activeSourceFilter]);
  const filteredDevices = useMemo(
    () =>
      filterDevices(
        inventory,
        {
          manufacturer: activeManufacturerFilter,
          status: statusFilter,
          source: activeSourceFilter,
        },
        now
      ),
    [inventory, activeManufacturerFilter, statusFilter, activeSourceFilter, now]
  );
  // Protocol twins nest under one lead per physical instrument (task
  // sk_rows_unfolded, owner-decided presentation). Grouped AFTER filtering,
  // so a filter that matches only the twin leaves it standing as its own row.
  const grouped = useMemo(() => groupProtocolTwins(filteredDevices), [filteredDevices]);
  const groupedTwinCount = filteredDevices.length - grouped.leads.length;
  // Keyed off the ACTIVE source (not the raw choice) so the default
  // primary-gateway filter also offers the "Clear filters" escape to all
  // gateways — otherwise a screen that opens filtered has no visible way out.
  const filtersActive =
    manufacturerFilter !== ALL_MANUFACTURERS ||
    statusFilter !== 'all' ||
    activeSourceFilter !== ALL_SOURCES;

  function clearFilters(): void {
    setManufacturerFilter(ALL_MANUFACTURERS);
    setStatusFilter('all');
    setSourceFilter(ALL_SOURCES);
  }

  return (
    <section data-testid="devices">
      <div className="page-head toolkit-entry-head">
        <div>
          <h1>Devices aboard {boat?.display_name ?? 'this boat'}</h1>
          <p className="page-sub">Names, roles, reporting status, and manuals at a glance.</p>
        </div>
        <div className="devices-head-actions">
          {/* The engines, tanks and batteries the devices report, and the
              owner's answer about each (TOM-409, cloud ADR 0312). */}
          <Link
            className="toolkit-button"
            data-testid="devices-open-equipment"
            to={`/dashboard/boats/${boatId}/equipment`}
          >
            Equipment
          </Link>
          <Link
            className="toolkit-button"
            data-testid="devices-open-toolkit"
            to={`/dashboard/boats/${boatId}/toolkit`}
          >
            <AnalyzerIcon /> Analyzer
          </Link>
        </div>
      </div>

      {state.kind === 'ready' && state.devices.length > 0 ? (
        <div
          className="device-inventory-controls"
          aria-label="Device list controls"
          data-testid="device-inventory-controls"
        >
          <div className="device-inventory-control device-inventory-control--sort">
            <label htmlFor="device-sort">Sort by</label>
            <select
              id="device-sort"
              value={sortBy}
              data-testid="device-sort"
              onChange={(event) => setSortBy(event.target.value as DeviceSortKey)}
            >
              <option value="name">Name</option>
              <option value="manufacturer">Manufacturer</option>
              <option value="source">Gateway source</option>
            </select>
          </div>

          <div className="device-inventory-control">
            <label htmlFor="device-manufacturer-filter">Manufacturer</label>
            <select
              id="device-manufacturer-filter"
              value={activeManufacturerFilter}
              data-testid="device-manufacturer-filter"
              onChange={(event) => setManufacturerFilter(event.target.value)}
            >
              <option value={ALL_MANUFACTURERS}>All manufacturers</option>
              {manufacturerOptions.map((option) => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="device-inventory-control device-inventory-control--status">
            <label htmlFor="device-status-filter">Status</label>
            <select
              id="device-status-filter"
              value={statusFilter}
              data-testid="device-status-filter"
              onChange={(event) => setStatusFilter(event.target.value as DeviceStatusFilter)}
            >
              <option value="all">All statuses</option>
              <option value="online">Online</option>
              <option value="offline">Offline / no data</option>
            </select>
          </div>

          <div className="device-inventory-control device-inventory-control--source">
            <label htmlFor="device-source-filter">Gateway source</label>
            <select
              id="device-source-filter"
              value={activeSourceFilter}
              data-testid="device-source-filter"
              onChange={(event) => setSourceFilter(event.target.value)}
            >
              <option value={ALL_SOURCES}>All gateways</option>
              {sourceOptions.map((option) => (
                <option value={option.value} key={option.value}>
                  {/* Name the primary in the list, so a screen that opens
                      filtered to it explains itself. */}
                  {option.value === primarySourceId ? `${option.label} · primary` : option.label}
                </option>
              ))}
            </select>
          </div>

          <p
            className="device-inventory-summary"
            aria-live="polite"
            data-testid="device-results-count"
          >
            {/* Counts speak in PHYSICAL instruments: a protocol twin nested
                under its lead is the same box, not another device. */}
            {filteredDevices.length === state.devices.length
              ? deviceCountLabel(grouped.leads.length)
              : `${grouped.leads.length} of ${deviceCountLabel(
                  groupProtocolTwins(state.devices).leads.length
                )}`}
            {groupedTwinCount > 0 ? ` · ${groupedTwinCount} grouped as protocol twins` : ''}
          </p>

          {filtersActive && filteredDevices.length > 0 ? (
            <button type="button" className="device-filter-clear" onClick={clearFilters}>
              Clear filters
            </button>
          ) : null}
        </div>
      ) : null}

      {state.kind === 'loading' ? <p className="muted">Loading devices…</p> : null}
      {state.kind === 'error' ? (
        <ul className="form-errors">
          <li>{state.message}</li>
        </ul>
      ) : null}

      {state.kind === 'ready' ? (
        state.devices.length === 0 ? (
          <div className="empty-card">
            <p>No devices reported yet.</p>
            <p className="muted">
              Neomaris Core ships its inventory the moment a device announces itself on the bus; the
              first snapshot arrives within seconds of discovery.
            </p>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="empty-card" data-testid="device-filter-empty">
            <p>No devices match these filters.</p>
            <button type="button" className="device-filter-clear" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        ) : (
          <DeviceSections
            devices={grouped.leads}
            boatId={boatId}
            sortBy={sortBy}
            now={now}
            twinsByLead={grouped.twinsByLead}
          />
        )
      ) : null}
    </section>
  );
}
